/**
 * The unified run timeline's pure merge: one total order over two
 * independently-paged planes (`/events` turns and `/logs` records), plus the
 * "log frontier" that stops the view telling the reader something false.
 * No DOM, no React — spec §5.2, §5.3, §5.4, §5.7 of the 2026-10-27 design.
 *
 * ## Total order
 *
 * 1. `ts` ascending, compared **lexicographically**.
 * 2. On an equal timestamp, a `turn` sorts before a `log`. `tsIso()` truncates
 *    nanoseconds to milliseconds, so same-millisecond ties are common, not
 *    exotic. Turn-first reads as cause-then-evidence: the `api_request` is the
 *    thing the adjacent log records describe.
 * 3. On a further tie, ascending source-array index. For turns that IS `sk`
 *    order — `/events` returns the run with `ScanIndexForward: true`, ascending
 *    by SK — and for logs it is the server-ordered position, which is the only
 *    correct answer (see "Keys" below).
 *
 * **The lexicographic compare is a chronological compare only because both
 * planes take their timestamp from the single `tsIso()` at
 * `lib/lambdas/shared/otlp.ts:47`** (called at `:82` for `api_request` events
 * and `:192` for general log records), which always ends in
 * `new Date(ms).toISOString()` — fixed-precision, always `Z`, and for every
 * representable year fixed-width `YYYY-MM-DDTHH:MM:SS.sssZ`. (The one
 * exception: past year 9999 `toISOString()` emits the expanded
 * `+011476-08-15T…` form, which sorts outside the normal range and is NOT
 * marked `unknownTime` — it needs a ≥21-digit nanosecond value, so it is
 * reachable only over the JSON wire. Deliberately not folded into
 * `unknownTime`: "no timestamp" and "an absurd timestamp" are different facts,
 * and one marker meaning both would say neither.)
 * Verified by reading that function, not inferred
 * from the field names matching. **If a future change gives either plane its
 * own formatter, this module mis-orders SILENTLY** — no error, no warning, just
 * a timeline whose adjacencies are wrong. (`dashboard/` does not import from
 * `lib/`, so that is a citation, not a dependency.)
 *
 * ## Keys
 *
 * A turn's key is `turn:${turn.sk}`; `sk` is genuinely unique. A log's key is
 * `log:${index}` — its position in the loaded array — because the response
 * strips the server's own `srcKey`/`srcLine` cursor components and `seq` is the
 * record's index *within its ingest request*, so two records from two different
 * requests in one session both carry `seq: 0`. Keying on `seq` is the live
 * `LogsPanel` bug this replaces: React warns and may reconcile one record's
 * expanded `<details>` onto another. **Never sort or key log rows by `seq`.**
 *
 * ## The frontier
 *
 * `/events` returns every turn of the run in one response; `/logs` returns one
 * ascending page with a cursor. So the client holds all the turns and a PREFIX
 * of the logs, and rendering a turn later than any loaded log puts it against a
 * log list that simply stops — from which the reader concludes nothing was
 * logged in between. That false correlation is what this view exists to kill.
 *
 * So while logs remain, `frontier` is the last loaded log's `ts_iso`; turns with
 * `ts > frontier` are **withheld and counted** (never silently dropped — the
 * caller announces them), and log rows are never withheld. When the logs are
 * exhausted the frontier lifts and every turn renders.
 *
 * **An UNKNOWN timestamp cannot define a frontier.** The frontier means "the
 * point up to which the log set is complete", and `UNKNOWN_TS` is the ABSENCE
 * of a timestamp rather than a position in time. So a last loaded log at the
 * epoch yields `frontier: null` and withholds nothing — the same answer, for
 * the same reason, as the empty-log case below. Without that guard a page of
 * `limit`+ epoch-dated records would put every real turn after the frontier and
 * render a logs-only timeline announcing the whole run's cost as "below the
 * loaded logs", which is exactly the empty-timeline outcome the empty-log
 * branch is defensive against.
 *
 * Three interlocking facts, all from the source rather than assumed:
 *
 * - **`<=` at the frontier is sound ONLY because of the turn-before-log
 *   tie-break above.** A log sharing a millisecond with a rendered turn could
 *   in principle be split onto the next page by the server's 3-tuple cursor;
 *   it cannot belong *above* that turn, because every turn at a millisecond
 *   sorts before every log at it. Change either ruling and the other stops
 *   holding — which is why this reason lives here, where an editor of the
 *   comparator will see it.
 * - **There is no phantom trailing page.** `next_cursor` is non-null only when
 *   `filtered.length > limit` (`lib/lambdas/query.ts:696-698`), so the frontier
 *   lifts on the last content-bearing page and no turn is ever stranded behind
 *   a `Load more` that would fetch nothing.
 * - **Checking only the LAST loaded log for `UNKNOWN_TS` is sufficient, not a
 *   heuristic**, because `/logs` returns records sorted ascending by `ts_iso`
 *   (`lib/lambdas/query.ts:802-803`), so epoch rows sort first and "the last
 *   loaded log is unknown-time" is equivalent to "every loaded log is". That
 *   equivalence is a dependency on the server's sort: if it ever changes, this
 *   one-value check degrades.
 *
 * ## Determinism
 *
 * The output order does not rest on `Array.prototype.sort` stability. The
 * comparator returns 0 only for an entry compared with itself: two distinct
 * entries either differ in `ts`, or share a `ts` and differ in `kindRank`, or
 * share both and are two rows of the same kind, which have distinct source
 * indices. So the total order is a strict one and repeated calls on the same
 * inputs produce the same rows in the same order, structurally.
 */
import type { LogLine, TurnEvent } from './queries';

/**
 * The value ingest's `tsIso()` USED to write — `new Date(0).toISOString()` —
 * when a producer sent no usable `timeUnixNano`. Since 2026-11-03 (#292) it
 * writes nothing of the kind: a record with no usable timestamp is refused at
 * ingest and reported in OTLP `partialSuccess`, so no NEW row can carry this
 * value. Rows stored before that deploy still can, until they age out (`EVT#`
 * turns on their 90-day TTL, S3 log objects on the 30-day lifecycle rule), and
 * this marker is kept for them. Such a row sorts to the very top and is MARKED
 * rather than hidden or re-sorted to the end: hiding a real record is the
 * silent failure this view exists to avoid, and the end is equally untrue about
 * when it happened while the top at least agrees with the stored value. This is
 * detection, not repair — Watchtower cannot recover a timestamp never sent.
 */
export const UNKNOWN_TS = '1970-01-01T00:00:00.000Z';

export type TimelineRow =
  | { kind: 'turn'; key: string; ts: string; unknownTime: boolean; turn: TurnEvent }
  | { kind: 'log'; key: string; ts: string; unknownTime: boolean; log: LogLine };

export interface MergedTimeline {
  rows: TimelineRow[];
  /** Turns later than `frontier`, withheld rather than rendered against a gap. */
  withheldTurnCount: number;
  /**
   * The derived cost of those withheld turns, summed. The notice quotes it,
   * because while the frontier is active the timeline's cost column does NOT
   * sum to the run header and a reader cross-checking the two would otherwise
   * conclude the column is wrong (spec §5.2). An unpriced turn contributes 0,
   * exactly as it contributes 0 to the header.
   */
  withheldCostUsd: number;
  /**
   * The last loaded log's `ts_iso` while logs REMAIN, else `null`.
   *
   * It is `null` iff the log set is exhausted — **not** iff nothing is
   * withheld. The two come apart whenever more logs remain but every turn is
   * already at or before the last loaded one: `frontier` is a string and
   * `withheldTurnCount` is 0. An earlier revision of this comment said "`null`
   * when nothing is withheld", and a caller who believed it would gate the
   * withheld-turn notice on `frontier !== null` and render "0 later turns are
   * below the loaded logs". **Gate the notice on the COUNT; gate the `Load
   * more` affordance on the frontier.**
   */
  frontier: string | null;
}

/** Sort rank for the kind tie-break: a turn precedes a log at one millisecond. */
const TURN_RANK = 0;
const LOG_RANK = 1;

interface Entry {
  row: TimelineRow;
  kindRank: number;
  /** Index in the row's OWN source array — the third and last sort key. */
  index: number;
}

/**
 * Merge the two planes into one ordered row list, applying the frontier.
 *
 * Neither input array is read out of order or written to: the sort runs over a
 * freshly built array of tagged entries, never over the caller's arrays.
 *
 * `logsExhausted` is the caller's reading of `next_cursor === null`. An EMPTY
 * log set is treated as exhausted whatever that flag says. The server cannot
 * produce empty-and-not-exhausted (a non-null cursor requires
 * `filtered.length > limit`), so that branch is purely defensive — and treating
 * it as exhausted is the deliberate choice, because withholding every turn
 * against a frontier derived from no logs would render an EMPTY timeline, which
 * is strictly worse than rendering the turns. An ALL-EPOCH log set gets the
 * same answer for the same reason (see "The frontier" above); unlike the empty
 * case, a producer really can cause it.
 */
export function mergeTimeline(
  turns: TurnEvent[],
  logs: LogLine[],
  logsExhausted: boolean,
): MergedTimeline {
  const lastLogTs = logs.length === 0 ? null : logs[logs.length - 1]!.ts_iso;
  // `lastLogTs === UNKNOWN_TS` means EVERY loaded log is epoch-dated, because
  // the server returns them ascending (`query.ts:802-803`) and the epoch sorts
  // first. An absent timestamp is not a position in time, so it defines no
  // frontier and withholds nothing.
  const frontier =
    logsExhausted || lastLogTs === null || lastLogTs === UNKNOWN_TS ? null : lastLogTs;

  const entries: Entry[] = [];
  let withheldTurnCount = 0;
  let withheldCostUsd = 0;

  turns.forEach((turn, index) => {
    const ts = turn.ts_iso;
    if (frontier !== null && ts > frontier) {
      withheldTurnCount += 1;
      // An unpriced turn contributes 0 because it contributes 0 to the run
      // header, and the two numbers exist to be cross-checked. `cost_usd` on an
      // unpriced turn is a placeholder zero by convention only, so the flag is
      // what is trusted here, never the value.
      withheldCostUsd += turn.unpriced ? 0 : turn.cost_usd;
      return;
    }
    entries.push({
      row: { kind: 'turn', key: `turn:${turn.sk}`, ts, unknownTime: ts === UNKNOWN_TS, turn },
      kindRank: TURN_RANK,
      index,
    });
  });

  // Log rows are never withheld: every loaded log is at or before the frontier
  // because the frontier IS the last of them AND the server returns them sorted
  // ascending by `ts_iso` (`lib/lambdas/query.ts:461-462`). The sort is the load
  // -bearing half — without it "the last one" would not be "the greatest one".
  logs.forEach((log, index) => {
    const ts = log.ts_iso;
    entries.push({
      row: { kind: 'log', key: `log:${index}`, ts, unknownTime: ts === UNKNOWN_TS, log },
      kindRank: LOG_RANK,
      index,
    });
  });

  entries.sort((a, b) => {
    if (a.row.ts !== b.row.ts) return a.row.ts < b.row.ts ? -1 : 1;
    if (a.kindRank !== b.kindRank) return a.kindRank - b.kindRank;
    return a.index - b.index;
  });

  return {
    rows: entries.map((e) => e.row),
    withheldTurnCount,
    withheldCostUsd,
    frontier,
  };
}
